import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import BeatCard from '../components/beats/BeatCard';
import VerseCard from '../components/verses/VerseCard';
import Pagination from '../components/Pagination';
import WelcomeOnboarding from '../components/onboarding/WelcomeOnboarding';
import { useHomeFeed } from '../hooks/useHomeFeed';
import { scrollToResultsIfStacked } from '../utils/scrollToResults';
import { Link } from 'react-router-dom';
import { CYPHER_COPY } from '../config/cypherChallenge';
import './Home.css';

const WAVE_BARS = 32;

const Home: React.FC = () => {
  const {
    activeTab,
    setActiveTab,
    beats,
    verses,
    loading,
    error,
    filters,
    setFilters,
    page,
    setPage,
    totalBeats,
    totalVerses,
    fetchContent,
    genres,
    states,
    pageSize,
  } = useHomeFeed();

  const [heroReady, setHeroReady] = useState(false);
  const resultsRef = useRef<HTMLElement | null>(null);
  const filtersTouchedRef = useRef(false);
  const pendingScrollRef = useRef(false);
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setHeroReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // Mark that the user changed a filter / tab (skip the initial mount).
  useEffect(() => {
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    pendingScrollRef.current = true;
  }, [filters.sort, filters.genre, filters.state, filters.beatType, activeTab]);

  // On mobile/tablet the filters sit above the feed — scroll to results
  // only after a load cycle finishes (avoids racing setLoading(true)).
  useEffect(() => {
    if (loading) {
      wasLoadingRef.current = true;
      return;
    }
    if (!wasLoadingRef.current || !pendingScrollRef.current) return;
    wasLoadingRef.current = false;
    pendingScrollRef.current = false;
    return scrollToResultsIfStacked(resultsRef.current);
  }, [loading]);

  const resultCount = activeTab === 'beats' ? totalBeats : totalVerses;
  const contentLabel = activeTab === 'beats' ? 'beats' : 'verses';

  const renderFeed = () => {
    if (loading) {
      return (
        <div className="home-preview__loading">
          <div className="home-preview__spinner" aria-label="Loading" />
          <p>Loading {activeTab}…</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="home-preview__error">
          <p>{error}</p>
          <button type="button" className="home-preview__retry" onClick={() => fetchContent()}>
            Try again
          </button>
        </div>
      );
    }

    if (activeTab === 'beats') {
      if (beats.length === 0) {
        return (
          <div className="home-preview__empty">
            <span className="home-preview__empty-icon" aria-hidden>
              🎵
            </span>
            <h2>No beats found</h2>
            <p>Try adjusting your filters to discover more music.</p>
          </div>
        );
      }
      return (
        <>
          <div className="home-preview__grid">
            {beats.map(beat => (
              <BeatCard
                key={beat.id}
                beat={beat}
                variant="dark"
                onVote={() => fetchContent()}
              />
            ))}
          </div>
          {totalBeats > pageSize && (
            <div className="home-preview__pagination">
              <Pagination page={page} setPage={setPage} total={totalBeats} pageSize={pageSize} />
            </div>
          )}
        </>
      );
    }

    if (verses.length === 0) {
      return (
        <div className="home-preview__empty">
          <span className="home-preview__empty-icon" aria-hidden>
            🎤
          </span>
          <h2>No verses found</h2>
          <p>Try adjusting your filters to discover more music.</p>
        </div>
      );
    }

    return (
      <>
        <div className="home-preview__grid">
          {verses.map(verse => (
            <VerseCard
              key={verse.id}
              verse={verse}
              variant="dark"
              onVote={() => fetchContent()}
            />
          ))}
        </div>
        {totalVerses > pageSize && (
          <div className="home-preview__pagination">
            <Pagination page={page} setPage={setPage} total={totalVerses} pageSize={pageSize} />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="home-preview">
      <Helmet>
        <title>DHH Collab | Home</title>
        <meta
          name="description"
          content="Discover, share, and collaborate on beats and verses with the DHH Collab community."
        />
      </Helmet>

      <div className="home-preview__page-bg" aria-hidden>
        <span className="home-preview__blob home-preview__blob--1" />
        <span className="home-preview__blob home-preview__blob--2" />
        <span className="home-preview__page-pattern" />
      </div>

      <section className={`home-preview__hero ${heroReady ? 'home-preview__hero--visible' : ''}`}>
        <div className="home-preview__hero-inner home-preview__content-wrap container-padding">
          <div className="home-preview__hero-text">
            <span className="home-preview__eyebrow">Your feed</span>
            <h1 className="home-preview__title">Discover</h1>
            <p className="home-preview__subtitle">
              Fresh beats and verses from producers and rappers across India.
            </p>
          </div>

          <div className="home-preview__wave-mini" aria-hidden>
            {Array.from({ length: WAVE_BARS }).map((_, i) => {
              const h = 20 + Math.abs(Math.sin(i * 0.6) * 70) + (i % 4) * 5;
              return (
                <span
                  key={i}
                  className="home-preview__wave-bar"
                  style={
                    {
                      '--bar-i': i,
                      '--bar-height': `${h}%`,
                    } as React.CSSProperties
                  }
                />
              );
            })}
          </div>
        </div>
      </section>

      <div className="home-preview__body home-preview__content-wrap container-padding">
        <WelcomeOnboarding />

        <aside className="home-cypher-promo" aria-labelledby="home-cypher-promo-title">
          <div className="home-cypher-promo__glow" aria-hidden />
          <div className="home-cypher-promo__content">
            <span className="home-cypher-promo__eyebrow">
              <span className="home-cypher-promo__dot" aria-hidden />
              New
            </span>
            <h2 id="home-cypher-promo-title" className="home-cypher-promo__title">
              {CYPHER_COPY.homePromoTitle}
            </h2>
            <p className="home-cypher-promo__body">{CYPHER_COPY.homePromoBody}</p>
          </div>
          <Link to="/cypher" className="home-cypher-promo__cta">
            {CYPHER_COPY.homePromoCta}
          </Link>
        </aside>

        <div className="home-preview__layout">
          <aside className="home-preview__sidebar" aria-label="Search and filter content">
            <div className="home-preview__sidebar-card">
              <div className="home-preview__sidebar-head">
                <h2 className="home-preview__sidebar-title">Browse</h2>
                <p className="home-preview__sidebar-hint">Refine results with the filters below.</p>
              </div>

              <div className="home-preview__filters">
                <p className="home-preview__filters-heading">Filters</p>
                <label className="home-preview__filter">
                  <span className="home-preview__filter-label">Sort</span>
                  <select
                    value={filters.sort}
                    onChange={e =>
                      setFilters(prev => ({ ...prev, sort: e.target.value as 'recent' | 'popular' }))
                    }
                  >
                    <option value="recent">Most Recent</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </label>

                {activeTab === 'beats' && (
                  <>
                    <label className="home-preview__filter">
                      <span className="home-preview__filter-label">Beat type</span>
                      <select
                        value={filters.beatType || ''}
                        onChange={e =>
                          setFilters(prev => ({
                            ...prev,
                            beatType: e.target.value as '' | 'cypher' | 'collaboration',
                          }))
                        }
                      >
                        <option value="">All beats</option>
                        <option value="cypher">Cypher challenges</option>
                        <option value="collaboration">Collaborations</option>
                      </select>
                    </label>

                    <label className="home-preview__filter">
                      <span className="home-preview__filter-label">Genre</span>
                      <select
                        value={filters.genre}
                        onChange={e => setFilters(prev => ({ ...prev, genre: e.target.value }))}
                      >
                        <option value="">All Genres</option>
                        {genres.map(genre => (
                          <option key={genre} value={genre}>
                            {genre}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="home-preview__filter">
                      <span className="home-preview__filter-label">State</span>
                      <select
                        value={filters.state}
                        onChange={e => setFilters(prev => ({ ...prev, state: e.target.value }))}
                      >
                        <option value="">All States</option>
                        {states.map(state => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                )}
              </div>

              <div className="home-preview__sidebar-footer">
                {!loading && !error && (
                  <p className="home-preview__result-count" aria-live="polite">
                    <strong>{resultCount}</strong> {contentLabel} found
                  </p>
                )}
              </div>
            </div>
          </aside>

          <main className="home-preview__main" ref={resultsRef}>
            <div className="home-preview__feed-tabs" role="tablist" aria-label="Content type">
              <button
                type="button"
                role="tab"
                id="home-tab-beats"
                aria-selected={activeTab === 'beats'}
                aria-controls="home-panel-beats"
                className={`home-preview__feed-tab ${activeTab === 'beats' ? 'home-preview__feed-tab--active' : ''}`}
                onClick={() => setActiveTab('beats')}
              >
                <span className="home-preview__feed-tab-value">
                  {activeTab === 'beats' && loading ? '—' : totalBeats}
                </span>
                <span className="home-preview__feed-tab-label">Beats</span>
              </button>
              <button
                type="button"
                role="tab"
                id="home-tab-verses"
                aria-selected={activeTab === 'verses'}
                aria-controls="home-panel-verses"
                className={`home-preview__feed-tab ${activeTab === 'verses' ? 'home-preview__feed-tab--active' : ''}`}
                onClick={() => setActiveTab('verses')}
              >
                <span className="home-preview__feed-tab-value">
                  {activeTab === 'verses' && loading ? '—' : totalVerses}
                </span>
                <span className="home-preview__feed-tab-label">Verses</span>
              </button>
            </div>

            <div
              id={activeTab === 'beats' ? 'home-panel-beats' : 'home-panel-verses'}
              role="tabpanel"
              aria-labelledby={`home-tab-${activeTab}`}
              className="home-preview__feed"
            >
              {renderFeed()}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Home;
